import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import EmptyState from '../../components/EmptyState';
import { Plus, FolderOpen, MapPin, Loader2, Trash2, FolderPlus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', location: '' });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/admin/projects`);
      setProjects(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/projects`, formData);
      toast.success('Project created successfully');
      setShowModal(false);
      setFormData({ name: '', description: '', location: '' });
      fetchProjects();
    } catch (error) {
      toast.error('Failed to create project');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API}/admin/projects/${projectId}`);
      toast.success('Project deleted');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  if (loading) {
    return (
      <Layout>
        <LoadingState message="Loading projects..." />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Projects</h1>
            <p className="text-slate-600 mt-1">Manage construction projects</p>
          </div>
          <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-md" data-testid="create-project-button">
            <Plus className="w-5 h-5" />
            <span>Add Project</span>
          </button>
        </div>

        {projects.length === 0 ? (
          <EmptyState 
            icon={FolderPlus}
            title="No projects yet"
            description="Get started by creating your first construction project."
            action={
              <button 
                onClick={() => setShowModal(true)} 
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span>Create Project</span>
              </button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div key={project.id} className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/app/projects/${project.id}/uploads`)} data-testid="project-card">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FolderOpen className="w-6 h-6 text-blue-600" />
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); deleteProject(project.id); }} className="p-2 hover:bg-red-50 rounded-lg transition-colors" data-testid="delete-project-button">
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{project.name}</h3>
                <p className="text-sm text-slate-600 mb-3 line-clamp-2">{project.description || 'No description'}</p>
                {project.location && (
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                    <MapPin className="w-4 h-4" />
                    <span>{project.location}</span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/uploads`); }} className="px-2 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors">Uploads</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/object-intel`); }} className="px-2 py-2 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors">Object Intel</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/measure`); }} className="px-2 py-2 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 transition-colors">2D Measure</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/3d`); }} className="px-2 py-2 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition-colors">3D Surface</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/reports`); }} className="col-span-2 px-2 py-2 bg-slate-600 text-white text-xs rounded-lg hover:bg-slate-700 transition-colors">Reports</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="create-project-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Create New Project</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Project Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" required data-testid="project-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" rows={3} data-testid="project-description-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
                <input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-testid="project-location-input" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors" data-testid="cancel-button">Cancel</button>
                <button type="submit" disabled={submitting} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="submit-project-button">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Creating...</span></> : <span>Create Project</span>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Projects;
